import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';

import { useAuth, type RegisterAgencyPayload } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const slugRegex = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

const registerSchema = z.object({
  email: z.string().email('Enter a valid email.'),
  password: z.string().min(12, 'At least 12 characters.'),
  agencyName: z.string().min(2, 'Agency name is required.'),
  slug: z
    .string()
    .min(2)
    .max(60)
    .regex(slugRegex, 'lowercase, alphanum, dashes only between letters/digits'),
  planTier: z.enum(['solo', 'pro', 'enterprise']),
  firstName: z.string().min(1, 'First name required.'),
  lastName: z.string().min(1, 'Last name required.'),
  phone: z.string().max(40).optional(),
});

type RegisterValues = z.infer<typeof registerSchema>;

export function RegisterAgency() {
  const { registerAgency, error, clearError, loading } = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
      agencyName: '',
      slug: '',
      planTier: 'solo',
      firstName: '',
      lastName: '',
      phone: '',
    },
  });

  const onSubmit = async (values: RegisterValues) => {
    setSubmitError(null);
    try {
      const payload: RegisterAgencyPayload = {
        email: values.email,
        password: values.password,
        agencyName: values.agencyName,
        slug: values.slug,
        planTier: values.planTier,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone?.trim() || undefined,
      };
      await registerAgency(payload);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      const m = (e as { message?: string })?.message || 'Registration failed.';
      setSubmitError(m);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-10 bg-background">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Register your agency</CardTitle>
          <CardDescription>
            Creates a tenant + the first agency administrator. Email must be
            unique across the platform.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="agencyName">Agency name</Label>
              <Input id="agencyName" {...register('agencyName')} />
              {errors.agencyName && (
                <p className="text-sm text-destructive">
                  {errors.agencyName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug (URL key)</Label>
              <Input id="slug" placeholder="acme" {...register('slug')} />
              {errors.slug && (
                <p className="text-sm text-destructive">{errors.slug.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="planTier">Plan</Label>
              <select
                id="planTier"
                {...register('planTier')}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="solo">Solo</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
              {errors.planTier && (
                <p className="text-sm text-destructive">
                  {errors.planTier.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <Input id="firstName" {...register('firstName')} />
              {errors.firstName && (
                <p className="text-sm text-destructive">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <Input id="lastName" {...register('lastName')} />
              {errors.lastName && (
                <p className="text-sm text-destructive">
                  {errors.lastName.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" {...register('email')} />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" {...register('phone')} />
            </div>

            {(submitError ?? error) && (
              <p className="text-sm text-destructive sm:col-span-2">
                {submitError ?? error}
              </p>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {loading ? 'Creating agency…' : 'Create agency'}
            </Button>
            <Link
              to="/login"
              className="text-sm text-muted-foreground hover:underline"
              onClick={clearError}
            >
              Already have an account? Sign in →
            </Link>
          </CardFooter>
        </form>
      </Card>
    </main>
  );
}